/**
 * PixiScene — thin React wrapper around the framework-agnostic PixiJS world.
 *
 * It owns the Pixi `Application` lifecycle (StrictMode-safe async init + full
 * teardown) and renders the low-res canvas, upscaled crisply via
 * `image-rendering: pixelated`. The canvas is sized/positioned by the
 * `transform` prop (a cover-fit scale + centre offset computed once by
 * [useCoverTransform] from the parent container) — the parent applies the *same*
 * transform to its DOM hotspot buttons so they stay pixel-aligned with the art.
 *
 * Visuals only: the canvas has `pointer-events: none`. All interaction lives in
 * the parent's transparent `<button>` layer; this component just reflects
 * `coverage` and `hovered` into the world imperatively.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { Application } from 'pixi.js'
import { createPixelWorld, type PixelWorld } from './pixel/world'
import {
  VIRTUAL_W,
  VIRTUAL_H,
  ZONE_ORDER,
  type ZoneKey,
} from './sceneLayout'
import type { ZoneCoverage } from './zoneState'

export interface CoverTransform {
  scale: number
  ox: number
  oy: number
}

/**
 * Cover-fit transform mapping the fixed 480×270 virtual space onto `ref`'s box:
 * scale so the art fills the container (cropping the overflow), then centre it.
 * Re-measures on resize. Returned values position both the canvas and the
 * sibling hotspot buttons.
 */
export function useCoverTransform(
  ref: RefObject<HTMLElement | null>,
): CoverTransform {
  const [t, setT] = useState<CoverTransform>({ scale: 1, ox: 0, oy: 0 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const cw = el.clientWidth
      const ch = el.clientHeight
      if (!cw || !ch) return
      const scale = Math.max(cw / VIRTUAL_W, ch / VIRTUAL_H)
      setT({
        scale,
        ox: (cw - VIRTUAL_W * scale) / 2,
        oy: (ch - VIRTUAL_H * scale) / 2,
      })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return t
}

/** Imperative command for the main character. */
export interface CharacterCommand {
  mode: 'idle' | 'scan' | 'die' | 'fly' | 'wave'
  x: number
}

export interface PixiSceneProps {
  coverage: Record<ZoneKey, ZoneCoverage>
  hovered: ZoneKey | null
  transform: CoverTransform
  /** true = day (sun up), false = night (moon + stars). */
  daytime: boolean
  /** Where the character should be / what it should do. */
  character: CharacterCommand
  /** The zone the character is currently engaging (drives the cat hiss). */
  activeZone: ZoneKey | null
  /** True once the suitcases have been flown off (hidden until travel insured). */
  travelGone: boolean
  /** Increment to summon the fire-brigade truck (car-on-fire click gag). */
  fireTruckTick: number
  /** Increment to send the fire-brigade truck back off-screen (upload modal closed). */
  fireTruckLeaveTick: number
  /** Number of insured zones (0..5) — drives the character's accessory ladder. */
  characterLevel: number
}

export default function PixiScene({
  coverage,
  hovered,
  transform,
  daytime,
  character,
  activeZone,
  travelGone,
  fireTruckTick,
  fireTruckLeaveTick,
  characterLevel,
}: PixiSceneProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null)
  const appRef = useRef<Application | null>(null)
  const worldRef = useRef<PixelWorld | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // Mirror latest props into refs so the async init + late callbacks apply
  // current values (the init effect's closure is captured on first render).
  const coverageRef = useRef(coverage)
  const hoveredRef = useRef(hovered)
  const transformRef = useRef(transform)
  const daytimeRef = useRef(daytime)
  const characterRef = useRef(character)
  const activeZoneRef = useRef(activeZone)
  const travelGoneRef = useRef(travelGone)
  const characterLevelRef = useRef(characterLevel)
  coverageRef.current = coverage
  hoveredRef.current = hovered
  transformRef.current = transform
  daytimeRef.current = daytime
  characterRef.current = character
  activeZoneRef.current = activeZone
  travelGoneRef.current = travelGone
  characterLevelRef.current = characterLevel

  // ─── Pixi app lifecycle (once; StrictMode-safe) ──────────────────────────
  useEffect(() => {
    let cancelled = false
    const root = rootRef.current
    if (!root) return

    void (async () => {
      const app = new Application()
      await app.init({
        width: VIRTUAL_W,
        height: VIRTUAL_H,
        antialias: false,
        background: 0x3b6ca8,
        resolution: 1,
        autoDensity: false,
        preference: 'webgl',
      })
      if (cancelled) {
        app.destroy({ removeView: true }, { children: true })
        return
      }
      const canvas = app.canvas
      canvas.style.position = 'absolute'
      canvas.style.display = 'block'
      canvas.style.imageRendering = 'pixelated'
      canvas.style.pointerEvents = 'none'
      canvasRef.current = canvas
      root.appendChild(canvas)

      const world = createPixelWorld(app)
      for (const z of ZONE_ORDER) world.setCoverage(z, coverageRef.current[z])
      world.setHovered(hoveredRef.current)
      world.setDaytime(daytimeRef.current)
      world.setCharacter(characterRef.current.mode, characterRef.current.x)
      world.setActiveZone(activeZoneRef.current)
      world.setGone('travel', travelGoneRef.current)
      world.setCharacterLevel(characterLevelRef.current)

      appRef.current = app
      worldRef.current = world
      sizeCanvas()
    })()

    return () => {
      cancelled = true
      worldRef.current?.destroy()
      worldRef.current = null
      appRef.current?.destroy({ removeView: true }, { children: true })
      appRef.current = null
      canvasRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Push coverage / hover into the world ────────────────────────────────
  useEffect(() => {
    const world = worldRef.current
    if (!world) return
    for (const z of ZONE_ORDER) world.setCoverage(z, coverage[z])
  }, [coverage])

  useEffect(() => {
    worldRef.current?.setHovered(hovered)
  }, [hovered])

  useEffect(() => {
    worldRef.current?.setDaytime(daytime)
  }, [daytime])

  useEffect(() => {
    worldRef.current?.setCharacter(character.mode, character.x)
  }, [character.mode, character.x])

  useEffect(() => {
    worldRef.current?.setActiveZone(activeZone)
  }, [activeZone])

  useEffect(() => {
    worldRef.current?.setGone('travel', travelGone)
  }, [travelGone])

  // Edge-triggered: every increment of `fireTruckTick` launches one truck run.
  // (Skip the initial 0 so we don't summon it on mount.)
  useEffect(() => {
    if (fireTruckTick > 0) worldRef.current?.commandFireTruck('extinguish')
  }, [fireTruckTick])

  // Edge-triggered: every increment of `fireTruckLeaveTick` sends the truck
  // back off-screen (no-op if it isn't on scene).
  useEffect(() => {
    if (fireTruckLeaveTick > 0) worldRef.current?.commandFireTruck('leave')
  }, [fireTruckLeaveTick])

  useEffect(() => {
    worldRef.current?.setCharacterLevel(characterLevel)
  }, [characterLevel])

  // ─── Size the canvas with the cover transform ────────────────────────────
  // Reads the latest transform from a ref so the post-init call (which fires
  // after the canvas finally exists) and resize updates both use current data.
  function sizeCanvas() {
    const c = canvasRef.current
    if (!c) return
    const t = transformRef.current
    c.style.width = `${VIRTUAL_W * t.scale}px`
    c.style.height = `${VIRTUAL_H * t.scale}px`
    c.style.left = `${t.ox}px`
    c.style.top = `${t.oy}px`
  }
  useEffect(sizeCanvas, [transform])

  return <div ref={rootRef} className="absolute inset-0 z-0 overflow-hidden" aria-hidden />
}
